import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useBOM } from '@/hooks/useBOM'
import { useSistemas } from '@/hooks/useSistemas'
import { useVersiones } from '@/hooks/useVersiones'
import { useCapturas } from '@/hooks/useCapturas'
import { useLineas } from '@/hooks/useLineas'
import { useImageUpload } from '@/hooks/useImageUpload'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Loader } from '@/components/common/Loader'
import { BomImagen } from './BomImagen'
import type { LightboxInfo } from './ImagenLightbox'
import { CaracRichEditor } from './CaracRichEditor'
import { SortableList } from './SortableList'
import { SortableGrid } from './SortableGrid'
import { FichaDocumento } from './FichaDocumento'
import { CopiarCaractPicker } from './CopiarCaractPicker'
import { fmtQty, renderRich, joinCaracteristicas, CaracAviso } from './fichaRender'
import { compressImageForUpload } from '@/utils/compressImage'
import type { BOMLinea, BloqueResumen, CapturaBOM } from '@/types/bomCaptura'
import './ficha.css'
import { useCanEditBom } from '@/hooks/useCanEditBom'

type Seleccion = { itemId: string; empresa: string; formula: string }

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}

// ─── Iconos (SVG inline) ──────────────────────────────────────────────────────
const IcoSearch = () => <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="9" cy="9" r="6" /><path d="m14 14 4 4" /></svg>
const IcoEye = () => <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1.5 10S4.5 4 10 4s8.5 6 8.5 6-3 6-8.5 6-8.5-6-8.5-6Z" /><circle cx="10" cy="10" r="2.5" /></svg>
const IcoPencil = () => <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M13.5 3.5 16.5 6.5 7 16H4v-3z" /></svg>
const IcoPrinter = () => <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M6 8V3h8v5" /><path d="M6 14H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2" /><rect x="6" y="12" width="8" height="5" rx="1" /></svg>

/** Spinner de carga: usa el Loader multicolor común. */
function Spinner({ label }: { label?: string }) {
  return <Loader label={label} />
}

/** Vista única e interactiva de la ficha técnica. Tres estados: buscador tipo
 *  Gemini → lista de versiones → ficha (con FABs). La impresión usa FichaDocumento. */
export default function FichaCanvas() {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [chosenItem, setChosenItem] = useState(params.sistemaId ?? '')
  const [sel, setSel] = useState<Seleccion | null>(() =>
    params.sistemaId && params.empresa && params.formula
      ? { itemId: params.sistemaId, empresa: params.empresa, formula: params.formula }
      : null,
  )
  // Quien no puede editar no entra en modo edición NUNCA, aunque algo intente
  // encenderlo: el estado se filtra al derivarlo, no sólo al pintar el botón.
  // Todo el modo edición de esta pantalla cuelga de este único valor, así que
  // apagarlo aquí apaga el reordenar, el subir imágenes, el editor de texto y
  // las capturas de una vez.
  const puedeEditar = useCanEditBom()
  const [editingRaw, setEditing] = useState(false)
  const editing = puedeEditar && editingRaw
  const [q, setQ] = useState('')
  const [openResults, setOpenResults] = useState(false)
  const dq = useDebounced(q, 300)

  const { sistemas, isLoading: sistemasLoading } = useSistemas(dq)
  const { versiones, isLoading: versionesLoading } = useVersiones(chosenItem)

  const id = sel?.itemId ?? ''
  const emp = sel?.empresa ?? ''
  const frm = sel?.formula ?? ''
  const { cabecera, lineas, capturas, resumen, isLoading } = useBOM(id, emp, frm)
  const { isMobile } = useBreakpoint()
  const caps = useCapturas(id, emp, frm)
  const lineasHook = useLineas(id, emp, frm)

  const [ordenLineas, setOrdenLineas] = useState<BOMLinea[]>(lineas)
  useEffect(() => { setOrdenLineas(lineas) }, [lineas])
  // Al entrar/cambiar de ficha, empezar siempre en modo visualización
  useEffect(() => { setEditing(false) }, [sel])

  const caracteristicas = useMemo(() => capturas.filter((c) => c.tipo === 'caracteristica'), [capturas])
  const manipulacion = useMemo(() => capturas.filter((c) => c.tipo === 'manipulacion'), [capturas])
  const bloque = (n: number) => resumen?.bloques.find((b) => b.numero === n)

  function onSearchChange(e: ChangeEvent<HTMLInputElement>) {
    setQ(e.target.value)
    setOpenResults(true)
    if (chosenItem) setChosenItem('')   // teclear vuelve a la búsqueda de artículo
  }

  function pickArticulo(itemId: string) {
    setChosenItem(itemId)
    setQ('')
    setOpenResults(false)
  }

  function elegirVersion(v: { empresa: string; formula: string }) {
    const next = { itemId: chosenItem, empresa: v.empresa, formula: v.formula }
    setSel(next)
    navigate(`/bom/${encodeURIComponent(next.itemId)}/${encodeURIComponent(next.empresa)}/${encodeURIComponent(next.formula)}`)
  }

  function resetBusqueda() {
    setSel(null)
    setChosenItem('')
    setQ('')
    setOpenResults(false)
    navigate('/bom')
  }

  return (
    <>
      <div className={`fc ${editing ? '' : 'is-view'}`}>
        {!sel ? (
          // ── Estado 1/2: buscador Gemini + lista de versiones ───────────────
          <div className="fc-hero">
            <h1 className="fc-hero__title">{t('bomCaptura.canvas.heroTitle')}</h1>
            <div className="fc-hero__search">
              <span className="fc-hero__ico"><IcoSearch /></span>
              <input
                value={q}
                onChange={onSearchChange}
                onFocus={() => setOpenResults(true)}
                onBlur={() => setTimeout(() => setOpenResults(false), 150)}
                placeholder={t('bomCaptura.canvas.heroPlaceholder')}
                autoFocus
              />
            </div>

            {!chosenItem && openResults && (sistemas.length > 0 || (dq.trim() !== '' && !sistemasLoading)) && (
              <div className="fc-hero__results">
                {sistemas.length > 0 ? (
                  sistemas.map((s) => (
                    <button key={s.itemId} type="button" onMouseDown={() => pickArticulo(s.itemId)}>
                      <b>{s.itemId}</b>{s.nombre ? <> · <small>{s.nombre}</small></> : null}
                    </button>
                  ))
                ) : (
                  <div className="fc-hero__noresults">{t('bomCaptura.canvas.sinResultados')}</div>
                )}
              </div>
            )}

            {chosenItem && (
              <div className="fc-versions">
                <div className="fc-versions__head">
                  <span className="fc-versions__item">{chosenItem}</span>
                  <span className="fc-versions__sub">{t('bomCaptura.canvas.elegirVersion')}</span>
                </div>
                {versionesLoading ? (
                  <Spinner label={t('bomCaptura.canvas.cargandoVersiones')} />
                ) : versiones.length === 0 ? (
                  <p className="fc-muted">{t('bomCaptura.canvas.sinVersiones')}</p>
                ) : (
                  <div className="fc-versions__grid">
                    {versiones.map((v) => (
                      <button key={`${v.empresa}|${v.formula}`} type="button" className="fc-version-card" onClick={() => elegirVersion(v)}>
                        <b>{v.formula}</b>
                        <span>{v.empresa}</span>
                        {(v.color || v.estilo) && (
                          <span className="fc-version-card__dim">{[v.color, v.estilo].filter(Boolean).join(' · ')}</span>
                        )}
                        <span className="fc-version-card__tipo">{v.tipo}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : isLoading ? (
          <div className="fc__empty"><Spinner label={t('bomCaptura.canvas.cargandoFicha')} /></div>
        ) : (
          <>
            {/* Cabecera del documento (logo + producto/descripción) como el PDF */}
            <header className="f-header fc-dochead">
              <div className="f-hd__logo"><img src="/logo.svg" alt="AIR-VAL Perfumes" /></div>
              <div className="f-hd__info">
                <div className="f-hd__field">
                  <span className="f-hd__label">PRODUCTO</span>
                  <span className="f-hd__value">{[sel.itemId, cabecera?.color, cabecera?.estilo].filter(Boolean).join('.')}</span>
                </div>
                <div className="f-hd__field f-hd__field--right">
                  <span className="f-hd__label">DESCRIPCIÓN</span>
                  <span className="f-hd__value">{cabecera?.nombre || sel.itemId}</span>
                </div>
              </div>
            </header>

            <div className="fc__grid">
              {/* Bloque 2 · Producto final */}
              <Panel title={t('bomCaptura.ficha.productoFinal')} badge={bloque(2)} className="fc-prod">
                <ImgEditable sel={sel} productnumber={sel.itemId} esProductoFinal editing={editing} className="fc-img-contain" />
              </Panel>

              {/* Bloque 4 · Tabla de componentes */}
              <Panel title={t('bomCaptura.ficha.componentes')} badge={bloque(4)} className="fc-tabla">
                {ordenLineas.length === 0 ? (
                  <p className="fc-muted">—</p>
                ) : (
                  <>
                    <div className="fc-comp-row fc-comp-head">
                      <span />
                      <span>REF. / {t('bomCaptura.ficha.especificacion')}</span>
                      <span className="fc-comp-qty">{t('bomCaptura.ficha.unidades')}</span>
                      <span />
                    </div>
                    {editing ? (
                      <SortableList
                        items={ordenLineas}
                        dark={false}
                        onReorder={(next) => { setOrdenLineas(next); lineasHook.reorder.mutate(next) }}
                      >
                        {(l) => <CompRow n={ordenLineas.findIndex((x) => x.id === l.id) + 1} l={l} t={t} />}
                      </SortableList>
                    ) : (
                      <div className="cap-list">
                        {lineas.map((l, i) => <CompRow key={l.id || l.itemNumber} n={i + 1} l={l} t={t} />)}
                      </div>
                    )}
                  </>
                )}
              </Panel>

              {/* Bloques 3 y 6 · Características + Proceso */}
              <div className="fc-right">
                <Panel title={t('bomCaptura.ficha.caracteristicas')} badge={bloque(3)} expandable={!isMobile}>
                  <CaracteristicasTexto items={caracteristicas} caps={caps} editing={editing} formula={frm} t={t} />
                </Panel>
                <Panel title={t('bomCaptura.ficha.proceso')} badge={bloque(6)} expandable={!isMobile}>
                  <ProcesoPanel
                    sel={sel} items={manipulacion} caps={caps} editing={editing}
                    vacio={t('bomCaptura.cabecera.imagenesVacio')} addLabel={t('bomCaptura.cabecera.imagenesAdd')} t={t}
                  />
                </Panel>
              </div>

              {/* Bloque 5 · Galería de componentes */}
              <Panel title={t('bomCaptura.ficha.componentes')} badge={bloque(5)} className="fc-galeria" expandable={!isMobile}>
                <div className="f-galeria__grid">
                  {lineas.map((l, i) => (
                    <div className="f-gcard" key={l.id || l.itemNumber}>
                      <div className="f-gcard-head">
                        <span className="f-clnum">{i + 1}</span>
                        <div className="f-clbody">
                          <b className="f-clref">{[l.itemNumber, l.color, l.estilo].filter(Boolean).join('.')}</b>
                          <span className="f-clname">{l.nombre || l.itemNumber}</span>
                        </div>
                      </div>
                      <ImgEditable
                        sel={sel} productnumber={l.itemNumber} editing={editing} className="f-gcard__img f-ph"
                        info={{ pos: i + 1, titulo: [l.itemNumber, l.color, l.estilo].filter(Boolean).join('.'), descripcion: l.nombre }}
                      />
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </>
        )}
      </div>

      {/* Botones flotantes (solo con ficha cargada); se ocultan al imprimir */}
      {sel && !isLoading && (
        <div className="fc-fabs">
          <button type="button" className="fc-fab" title={t('bomCaptura.canvas.nuevaBusqueda')} onClick={resetBusqueda}><IcoSearch /></button>
          {puedeEditar && (
            <button type="button" className="fc-fab" title={editing ? t('bomCaptura.canvas.ver') : t('bomCaptura.canvas.editar')} onClick={() => setEditing((v) => !v)}>
              {editing ? <IcoEye /> : <IcoPencil />}
            </button>
          )}
          <button
            type="button"
            className="fc-fab fc-fab--accent fc-fab--muted"
            title={t('bomCaptura.ficha.imprimir')}
            onClick={() => window.print()}
          >
            <IcoPrinter />
          </button>
        </div>
      )}

      {/* Documento A4 dedicado para impresión (oculto en pantalla) */}
      {sel && !isLoading && (
        <div className="fc-print">
          <FichaDocumento itemId={sel.itemId} empresa={sel.empresa} formula={sel.formula}
            cabecera={cabecera} lineas={lineas} capturas={capturas} />
        </div>
      )}
    </>
  )
}

// ─── Subcomponentes ───────────────────────────────────────────────────────────

function Panel({ title, badge, className, children, expandable = false }: {
  title: string; badge?: BloqueResumen; className?: string; children: ReactNode; expandable?: boolean
}) {
  const { t } = useTranslation()
  const [full, setFull] = useState(false)

  useEffect(() => {
    if (!full) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFull(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])

  return (
    <section className={`fc-panel ${className ?? ''} ${full ? 'fc-panel--full' : ''}`}>
      <header className="fc-panel__head">
        {expandable ? (
          <button
            type="button"
            className="fc-panel__title fc-panel__title--btn"
            onClick={() => setFull((v) => !v)}
            title={t(full ? 'bomCaptura.ficha.cerrarCompleto' : 'bomCaptura.ficha.verCompleto')}
          >
            <span>{title}</span>
            {!full && <span className="fc-panel__expand" aria-hidden="true">⛶</span>}
          </button>
        ) : (
          <span className="fc-panel__title">{title}</span>
        )}
        {badge && !full && (
          <span className="fc-panel__badge" title={badge.detalle} style={{ color: badge.estado === 'listo' ? '#7ee2a8' : '#ffd479' }}>
            {badge.estado === 'listo' ? '✓' : '⚠'}
          </span>
        )}
        {full && (
          <button
            type="button"
            className="fc-panel__close"
            onClick={() => setFull(false)}
            title={t('bomCaptura.ficha.cerrarCompleto')}
            aria-label={t('bomCaptura.ficha.cerrarCompleto')}
          >
            ✕
          </button>
        )}
      </header>
      <div className="fc-panel__body">{children}</div>
    </section>
  )
}

function CompRow({ n, l, t }: { n: number; l: BOMLinea; t: (k: string) => string }) {
  return (
    <div className="fc-comp-row">
      <span className="f-dot">{n}</span>
      <div className="fc-comp-refspec">
        <b className="fc-comp-ref">{l.itemNumber}</b>
        {l.nombre && <span className="fc-comp-spec">{l.nombre}</span>}
      </div>
      <span className="fc-comp-qty">{fmtQty(l.quantity)}</span>
      {!l.tieneImagen ? <span className="fc-tag-warn">{t('bomCaptura.canvas.sinImagen')}</span> : <span />}
    </div>
  )
}

/** Zona clicable "Añadir imagen" usada como placeholder de imagen vacía en edición. */
function AddImageZone({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button type="button" className="fc-addimg" onClick={onClick}>
      <span className="fc-addimg__plus">＋</span>
      <span>{t('bomCaptura.canvas.addImage')}</span>
    </button>
  )
}

/** Imagen de un producto (final o componente) con reemplazo in-place. En edición y
 *  sin imagen muestra una zona "Añadir imagen"; en vista, un recuadro neutro. */
function ImgEditable({ sel, productnumber, esProductoFinal, editing, className, info }: {
  sel: Seleccion; productnumber: string; esProductoFinal?: boolean; editing: boolean; className?: string; info?: LightboxInfo
}) {
  const qc = useQueryClient()
  const { upload, status } = useImageUpload(sel.itemId, sel.empresa, sel.formula)
  const ref = useRef<HTMLInputElement>(null)

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const opt = await compressImageForUpload(f)
    const imgKey = ['bom-img', sel.itemId, sel.empresa, sel.formula, productnumber]
    // Optimista: muestra la imagen en local al instante; D365 se sube en 2.º plano.
    qc.setQueryData(imgKey, await fileToDataUrl(opt))
    e.target.value = ''
    try {
      await upload({ productnumber, esProductoFinal: Boolean(esProductoFinal), file: opt })
      // La imagen ya se ve en local; solo refrescamos badges (tieneImagen/estado).
      qc.invalidateQueries({ queryKey: ['bom', sel.itemId, sel.empresa, sel.formula] })
    } catch {
      // Si la subida falla, revertir a lo que haya en D365.
      qc.invalidateQueries({ queryKey: imgKey })
    }
  }

  return (
    <div className="img-edit">
      <BomImagen
        itemId={sel.itemId} empresa={sel.empresa} formula={sel.formula} productnumber={productnumber}
        dark={false} zoomable info={info} className={className} hideWhenEmpty={!editing}
        placeholder={editing ? <AddImageZone onClick={() => ref.current?.click()} /> : undefined}
      />
      {editing && (
        <>
          <button type="button" className="img-edit__btn edit-only" disabled={status === 'uploading'} onClick={() => ref.current?.click()} aria-label="Cambiar imagen">
            {status === 'uploading' ? '…' : '✎'}
          </button>
          <input ref={ref} type="file" accept="image/*" hidden onChange={onPick} />
        </>
      )}
    </div>
  )
}

/** Características de producción: editor de texto enriquecido (negrita/cursiva/subrayado),
 *  una viñeta por línea. Guardar consolida en el primer registro; Borrar elimina todos. */
function CaracteristicasTexto({ items, caps, editing, formula, t }: {
  items: CapturaBOM[]
  caps: ReturnType<typeof useCapturas>
  editing: boolean
  formula: string
  t: (k: string) => string
}) {
  const joined = joinCaracteristicas(items)
  const [text, setText] = useState(joined)
  const [pickerOpen, setPickerOpen] = useState(false)
  useEffect(() => { setText(joined) }, [joined])
  const dirty = text !== joined
  const saving = caps.add.isPending || caps.update.isPending || caps.remove.isPending
  const puedeGuardar = text.trim().length > 0 && dirty && !saving

  async function save() {
    if (!text.trim()) return
    const clean = text.replace(/\r\n/g, '\n').trim()
    if (items.length === 0) {
      await caps.add.mutateAsync({ tipo: 'caracteristica', nombre: '', descripcion: clean, secuencia: 1 })
    } else {
      await caps.update.mutateAsync({ id: items[0].id, body: { nombre: '', descripcion: clean, secuencia: 1 } })
      for (const extra of items.slice(1)) await caps.remove.mutateAsync(extra.id)
    }
  }

  async function borrar() {
    for (const r of items) await caps.remove.mutateAsync(r.id)
    setText('')
  }

  if (!editing) {
    return (
      <>
        <CaracAviso />
        {joined ? <div className="fc-carac-view">{renderRich(joined)}</div> : <p className="fc-muted">—</p>}
      </>
    )
  }
  return (
    <div className="fc-carac-edit edit-only">
      <CaracRichEditor
        value={text}
        onChange={setText}
        placeholder={t('bomCaptura.canvas.caracPlaceholder')}
      />
      <div className="fc-carac-foot">
        <button type="button" className="fc__btn fc__btn--danger" onClick={borrar} disabled={items.length === 0 || saving}>
          {t('bomCaptura.cabecera.eliminar')}
        </button>
        <button type="button" className="fc__btn" onClick={() => setPickerOpen(true)} disabled={saving}>
          {t('bomCaptura.copiar.boton')}
        </button>
        <span className="fc__spacer" />
        <button type="button" className="fc__btn fc__btn--primary" onClick={save} disabled={!puedeGuardar}>
          {t('bomCaptura.canvas.guardar')}
        </button>
      </div>
      {pickerOpen && (
        <CopiarCaractPicker
          currentFormula={formula}
          onSelect={(texto) => setText(texto)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}

/** Proceso de manipulación: rejilla de pasos (nº · foto · descripción) editable
 *  in-place, reordenable; alta con celda "+" redonda (con ≥1 paso) o botón (con 0). */
function ProcesoPanel({ sel, items, caps, editing, vacio, addLabel, t }: {
  sel: Seleccion
  items: CapturaBOM[]
  caps: ReturnType<typeof useCapturas>
  editing: boolean
  vacio: string
  addLabel: string
  t: (k: string) => string
}) {
  const [orden, setOrden] = useState<CapturaBOM[]>(items)
  useEffect(() => { setOrden(items) }, [items])
  const [mostrarAlta, setMostrarAlta] = useState(false)
  const [nombre, setNombre] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [imgData, setImgData] = useState<string | null>(null)

  const next = (items.reduce((m, c) => Math.max(m, c.secuencia), 0) || 0) + 1

  async function submit() {
    if (!nombre.trim() && !descripcion.trim()) return
    await caps.add.mutateAsync({
      tipo: 'manipulacion', nombre: nombre.trim(), descripcion: descripcion.trim(), secuencia: next,
      imagen_base64: imgData ? imgData.split(',')[1] ?? null : null,
    })
    setNombre(''); setDescripcion(''); setImgData(null); setMostrarAlta(false)
  }

  const addForm = (
    <div className="cap-add edit-only">
      <label className="cap-add__img">
        {imgData
          ? <img src={imgData} alt="" />
          : <span className="cap-add__imgph">📷 {t('bomCaptura.canvas.addImage')}</span>}
        <input
          type="file" accept="image/*" hidden
          onChange={async (e) => {
            const f = e.target.files?.[0]
            if (f) setImgData(await fileToDataUrl(await compressImageForUpload(f)))
            e.target.value = ''
          }}
        />
      </label>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t('bomCaptura.cabecera.nombrePlaceholder')} />
      <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder={t('bomCaptura.cabecera.descripcionPlaceholder')} />
      <div className="cap-add__foot">
        <span className="fc__spacer" />
        <button type="button" className="fc__btn" onClick={() => { setMostrarAlta(false); setNombre(''); setDescripcion(''); setImgData(null) }}>
          {t('bomCaptura.canvas.cancelar')}
        </button>
        <button type="button" className="fc__btn fc__btn--primary" onClick={submit} disabled={caps.add.isPending || (!nombre.trim() && !descripcion.trim())}>
          {addLabel}
        </button>
      </div>
    </div>
  )

  if (!editing) {
    return orden.length === 0
      ? <p className="fc-muted">{vacio}</p>
      : <div className="fc-proceso-grid">{orden.map((c) => <ProcesoStep key={c.id} sel={sel} c={c} caps={caps} editing={false} t={t} />)}</div>
  }

  // Edición: la rejilla se mantiene visible; el alta se abre como modal centrado
  // sobre el fondo oscurecido (la rejilla se sigue viendo detrás).
  return (
    <>
      {orden.length === 0 ? (
        <button type="button" className="fc-add-toggle" onClick={() => setMostrarAlta(true)}>＋ {t('bomCaptura.canvas.addStep')}</button>
      ) : (
        <div className="fc-proceso-wrap">
          <SortableGrid
            items={orden}
            className="fc-proceso-grid"
            onReorder={(n) => { setOrden(n); caps.reorder.mutate(n) }}
            after={
              <button type="button" className="fc-step-add" onClick={() => setMostrarAlta(true)} aria-label={t('bomCaptura.canvas.addStep')} title={t('bomCaptura.canvas.addStep')}>＋</button>
            }
          >
            {(c) => <ProcesoStep sel={sel} c={c} caps={caps} editing t={t} />}
          </SortableGrid>
        </div>
      )}
      {mostrarAlta && (
        <div
          className="fc-modal-overlay edit-only"
          onClick={() => { setMostrarAlta(false); setNombre(''); setDescripcion(''); setImgData(null) }}
        >
          <div className="fc-modal-card" onClick={(e) => e.stopPropagation()}>{addForm}</div>
        </div>
      )}
    </>
  )
}

/** Tarjeta de un paso de proceso: nº (arriba-izq), imagen reemplazable y descripción. */
function ProcesoStep({ sel, c, caps, editing, t }: {
  sel: Seleccion
  c: CapturaBOM
  caps: ReturnType<typeof useCapturas>
  editing: boolean
  t: (k: string) => string
}) {
  const qc = useQueryClient()
  const ref = useRef<HTMLInputElement>(null)
  const [nombre, setNombre] = useState(c.nombre)
  const [descripcion, setDescripcion] = useState(c.descripcion)
  const [confirmDel, setConfirmDel] = useState(false)
  useEffect(() => { setNombre(c.nombre); setDescripcion(c.descripcion) }, [c.id, c.nombre, c.descripcion])

  async function replaceImg(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const url = await fileToDataUrl(await compressImageForUpload(f))
    const capKey = ['bom-img-cap', sel.itemId, sel.empresa, sel.formula, c.id]
    // Optimista: muestra la imagen en local; D365 se actualiza en 2.º plano.
    qc.setQueryData(capKey, url)
    e.target.value = ''
    caps.update.mutate(
      { id: c.id, body: { imagen_base64: url.split(',')[1] ?? null } },
      { onError: () => qc.invalidateQueries({ queryKey: capKey }) },
    )
  }

  function saveText() {
    if (nombre !== c.nombre || descripcion !== c.descripcion) {
      caps.update.mutate({ id: c.id, body: { nombre, descripcion } })
    }
  }

  return (
    <div className="f-step fc-step">
      <span className="f-dot fc-step__dot">{c.secuencia}</span>
      <div className="img-edit fc-step__imgwrap">
        <BomImagen
          itemId={sel.itemId} empresa={sel.empresa} formula={sel.formula} capturaId={c.id}
          dark={false} zoomable className="f-step__img f-ph" hideWhenEmpty={!editing}
          info={{ pos: c.secuencia, titulo: c.nombre, descripcion: c.descripcion }}
          placeholder={editing ? <AddImageZone onClick={() => ref.current?.click()} /> : undefined}
        />
        {editing && (
          <>
            <button type="button" className="img-edit__btn edit-only" onClick={() => ref.current?.click()} aria-label="Cambiar imagen">
              ✎
            </button>
            <input ref={ref} type="file" accept="image/*" hidden onChange={replaceImg} />
          </>
        )}
      </div>
      {editing ? (
        <>
          <input className="fc-step__title-input" value={nombre} onChange={(e) => setNombre(e.target.value)} onBlur={saveText} placeholder={t('bomCaptura.cabecera.nombrePlaceholder')} />
          <textarea className="fc-step__desc-input" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} onBlur={saveText} placeholder={t('bomCaptura.cabecera.descripcionPlaceholder')} />
          <button type="button" className="fc-step__del edit-only" onClick={() => setConfirmDel(true)} disabled={caps.remove.isPending} aria-label="Eliminar">✕</button>
        </>
      ) : (
        <div className="fc-step__text">
          {c.nombre && <b>{c.nombre}</b>}
          <p>{c.descripcion}</p>
        </div>
      )}
      {confirmDel && createPortal(
        <div className="fc-modal-overlay" onClick={() => setConfirmDel(false)}>
          <div className="fc-modal-card fc-modal-card--sm" onClick={(e) => e.stopPropagation()}>
            <p className="fc-confirm-msg">{t('bomCaptura.canvas.borrarPasoConfirm')}</p>
            <p className="fc-confirm-item">
              <span className="fc-confirm-num">{c.secuencia}.</span>
              <span className="fc-confirm-name">{c.nombre.trim() || t('bomCaptura.canvas.pasoSinNombre')}</span>
            </p>
            <div className="cap-add__foot">
              <span className="fc__spacer" />
              <button type="button" className="fc__btn" onClick={() => setConfirmDel(false)}>{t('bomCaptura.canvas.cancelar')}</button>
              <button type="button" className="fc__btn fc__btn--danger" onClick={() => { caps.remove.mutate(c.id); setConfirmDel(false) }}>{t('bomCaptura.canvas.borrar')}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
